import { createContext, useContext, useEffect, useState, useCallback } from "react";
import api, { formatApiErrorDetail } from "./api";
import { stampSessionStart, clearSessionStart } from "../components/SessionMonitor";

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
    const [user, setUser] = useState(null); // null=loading, false=anon, object=auth
    const [error, setError] = useState("");

    const fetchMe = useCallback(async () => {
        try {
            const { data } = await api.get("/auth/me");
            setUser(data);
        } catch {
            setUser(false);
        }
    }, []);

    useEffect(() => { fetchMe(); }, [fetchMe]);

    const adminLogin = async (email, password, rememberMe = false) => {
        setError("");
        try {
            const { data } = await api.post("/auth/admin/login", { email, password, remember_me: rememberMe });
            // Non-exempt admins get a 2FA challenge back instead of a session.
            if (data?.two_factor_required) {
                return {
                    twoFactorRequired: true,
                    challengeToken: data.challenge_token,
                    emailMasked: data.email_masked,
                    resendIn: data.resend_in,
                    expiresInMinutes: data.expires_in_minutes,
                    emailSent: !!data.email_sent,
                };
            }
            if (data.token) localStorage.setItem("cc_token", data.token);
            stampSessionStart();
            setUser({ ...data.user });
            return true;
        } catch (e) {
            setError(formatApiErrorDetail(e.response?.data?.detail) || e.message);
            return false;
        }
    };

    const adminVerify2FA = async (challengeToken, code, rememberMe = false) => {
        setError("");
        try {
            const { data } = await api.post("/auth/admin/verify-2fa", {
                challenge_token: challengeToken,
                code,
                remember_me: rememberMe,
            });
            if (data.token) localStorage.setItem("cc_token", data.token);
            stampSessionStart();
            setUser({ ...data.user });
            return true;
        } catch (e) {
            setError(formatApiErrorDetail(e.response?.data?.detail) || e.message);
            return false;
        }
    };

    const adminResend2FA = async (challengeToken) => {
        setError("");
        try {
            const { data } = await api.post("/auth/admin/resend-2fa", { challenge_token: challengeToken });
            return { ok: true, ...data };
        } catch (e) {
            const msg = formatApiErrorDetail(e.response?.data?.detail) || e.message;
            setError(msg);
            return { ok: false, detail: msg };
        }
    };

    const voterLogin = async (voter_id, pin, rememberMe = false) => {
        setError("");
        try {
            const { data } = await api.post("/auth/voter/login", { voter_id, pin, remember_me: rememberMe });
            if (data.token) localStorage.setItem("cc_token", data.token);
            stampSessionStart();
            setUser({ ...data.user });
            return true;
        } catch (e) {
            setError(formatApiErrorDetail(e.response?.data?.detail) || e.message);
            return false;
        }
    };

    const logout = async () => {
        try { await api.post("/auth/logout"); } catch {}
        localStorage.removeItem("cc_token");
        clearSessionStart();
        setUser(false);
    };

    return (
        <AuthContext.Provider value={{ user, error, setError, adminLogin, adminVerify2FA, adminResend2FA, voterLogin, logout, refresh: fetchMe }}>
            {children}
        </AuthContext.Provider>
    );
}

export const useAuth = () => useContext(AuthContext);
